/**
 * The operations a studio draft changes by: small, typed, addressed by KEY
 * (never by name), applied in atomic batches by `applyStudioOps` (apply.ts).
 * The person's direct edits and the agent's compiled changes are the same
 * ops, so one reducer, one judge and one receipt serve both.
 *
 * This is the strict form. The wire form (`StudioOpWire`) differs in three
 * ways, read by `readStudioOps` (read-ops.ts): every formula tree is a flat
 * list, a blank string reads as absent, and every array is capped. A client
 * sends strict ops through `studioOpsOnWire`, the reader's inverse.
 */
import type {
  SetupDraftField,
  SetupDraftLines,
  SetupDraftRecord,
  SetupDraftSeedRow,
  SetupDraftTile,
} from '../compose-author';
import type { AgentLanguageTag } from '../agent-language';
import type { CoversAll } from '../covers-all';
import type {
  CriticCheck,
  StudioAppMap,
  StudioBoardCard,
  StudioFrom,
  StudioImport,
  StudioNote,
  StudioNotYet,
  StudioRecord,
  StudioRole,
  StudioSource,
  StudioDraft,
} from './draft';
import { STUDIO_LIMITS } from './draft';
import type { StudioPartRef } from './paths';

/** Where a field sits: a record, and the chain of `lines` field keys down from it (at most LINES_MAX_DEPTH). */
export type StudioFieldAt = { record: string; within?: string[] };

/**
 * What leaving a part out does to what depends on it: refuse (the default)
 * while anything reads it; `with_serving`, take what only shows or serves it
 * (its cards, notes, bindings) and refuse while a field or a money rule
 * needs it, as a removal the person asked for does; or take the dependents too.
 */
export type StudioCascade = 'refuse' | 'with_serving' | 'with_dependents';

/** A field as an op brings it: its key may be left to the reducer, which mints one from the label. */
export type StudioFieldInput = Omit<SetupDraftField, 'key' | 'lines'> & {
  key?: string;
  lines?: Omit<SetupDraftLines, 'fields'> & { fields: StudioFieldInput[] };
};

/** A record's own card as an op brings it: its key may be minted from the label. */
export type StudioTileInput = SetupDraftTile & { key?: string };

/** The record members `update_record` may set. Kind never changes: remove the record and add it again. */
export type StudioRecordProps = Pick<
  SetupDraftRecord,
  'entryNoun' | 'direction' | 'icon' | 'headlineFieldKey' | 'dateFieldKey' | 'measureFieldKey' | 'signFieldKey' | 'focusFieldKey' | 'capture' | 'layout' | 'rules' | 'views' | 'posting' | 'accounts' | 'stockItems' | 'chargePlan' | 'approvalChain' | 'tasks'
>;

export const STUDIO_RECORD_CLEARABLE = ['icon', 'dateFieldKey', 'measureFieldKey', 'signFieldKey', 'focusFieldKey', 'capture', 'layout', 'rules', 'views', 'posting', 'stockItems', 'chargePlan', 'approvalChain', 'tasks'] as const;

export const STUDIO_FIELD_CLEARABLE = [
  'required',
  'sticky',
  'help',
  'options',
  'targetRecordKey',
  'filter',
  'maxChosen',
  'accounts',
  'shape',
  'units',
  'quickAmounts',
  'formula',
  'startsWith',
  'startsOnce',
  'showWhen',
  'editableWhen',
  'mustBe',
  'openRow',
] as const;

/** What `update_field` may set: any member but the key, and of a lines field its words, count field and most. */
export type StudioFieldSet = Partial<Omit<SetupDraftField, 'key' | 'lines'>> & {
  lines?: { noun?: string; nouns?: string; countFrom?: string; max?: number; start?: number };
};

export type StudioSetWorkspace = {
  op: 'set_workspace';
  name?: string;
  currency?: string;
  language?: AgentLanguageTag;
  place?: string;
  does?: string;
  /** The business's own site: an `https` URL (`SITE_NOT_URL` otherwise). */
  site?: string;
  clear?: Array<'place' | 'does' | 'site'>;
};

export type StudioAddRecord = {
  op: 'add_record';
  key?: string;
  name: string;
  kind: StudioRecord['kind'];
  entryNoun?: string;
  direction?: SetupDraftRecord['direction'];
  icon?: SetupDraftRecord['icon'];
  fields: StudioFieldInput[];
  headlineFieldKey?: string;
  dateFieldKey?: string;
  measureFieldKey?: string;
  signFieldKey?: string;
  focusFieldKey?: string;
  capture?: SetupDraftRecord['capture'];
  layout?: SetupDraftRecord['layout'];
  seedRows?: SetupDraftSeedRow[];
  tiles?: StudioTileInput[];
  /** Rules on the whole entry (F13), keyed by field keys. */
  rules?: SetupDraftRecord['rules'];
  /** Saved views of its rows (6.7 of docs/plans/workbook-map.md), by field keys. */
  views?: SetupDraftRecord['views'];
  /** The money rule (step 2.3 of docs/plans/ledger.md): a posting pattern and its roles, by field keys. */
  posting?: SetupDraftRecord['posting'];
  /** On the Accounts list only: its Type field (L15). */
  accounts?: SetupDraftRecord['accounts'];
  /** On a list whose rows are stock items (Phase 9): its unit field and its pack fields. */
  stockItems?: SetupDraftRecord['stockItems'];
  /** On a list of parties billed on a schedule (L23): the charge plan. */
  chargePlan?: SetupDraftRecord['chargePlan'];
  /** Which entries wait for approval, and its steps (L55). */
  approvalChain?: SetupDraftRecord['approvalChain'];
  /** The tasks its entries raise (step 9.3). */
  tasks?: SetupDraftRecord['tasks'];
  /** The record it goes before; the end when absent. */
  before?: string;
  from?: StudioFrom[];
};

export type StudioUpdateRecord = { op: 'update_record'; record: string; clear?: Array<(typeof STUDIO_RECORD_CLEARABLE)[number]> } & Partial<StudioRecordProps>;

export type StudioRename = { op: 'rename'; target: StudioPartRef; name: string };

export type StudioRemoveRecord = { op: 'remove_record'; record: string; cascade?: StudioCascade };

export type StudioAddField = StudioFieldAt & { op: 'add_field'; field: StudioFieldInput; before?: string; from?: StudioFrom[] };

export type StudioUpdateField = StudioFieldAt & {
  op: 'update_field';
  field: string;
  set: StudioFieldSet;
  clear?: Array<(typeof STUDIO_FIELD_CLEARABLE)[number]>;
};

export type StudioRemoveField = StudioFieldAt & { op: 'remove_field'; field: string; cascade?: StudioCascade };

export type StudioMoveField = StudioFieldAt & { op: 'move_field'; field: string; before?: string };

export type StudioSeedList = { op: 'seed_list'; record: string; rows: SetupDraftSeedRow[]; mode: 'replace' | 'append' };

/** The ops of the workspace, its records and lists, and their fields. */
export type StudioRecordOp =
  | StudioSetWorkspace
  | StudioAddRecord
  | StudioUpdateRecord
  | StudioRename
  | StudioRemoveRecord
  | StudioAddField
  | StudioUpdateField
  | StudioRemoveField
  | StudioMoveField
  | StudioSeedList;

/** Leaving these out has no dependents: nothing else reads a card, a role, an import, a note or a not-yet item. */
export type StudioPartOp =
  | { op: 'add_card'; record: string; card: StudioTileInput; from?: StudioFrom[] }
  | { op: 'remove_card'; record: string; card: string }
  | { op: 'add_board_card'; card: Omit<StudioBoardCard, 'key'> & { key?: string }; from?: StudioFrom[] }
  | { op: 'remove_board_card'; card: string }
  | { op: 'add_role'; role: Omit<StudioRole, 'key'> & { key?: string }; from?: StudioFrom[] }
  | {
      op: 'update_role';
      role: string;
      purpose?: string;
      people?: string[];
      base?: StudioRole['base'];
      permissions?: StudioRole['permissions'];
      access?: StudioRole['access'];
      clear?: Array<'purpose'>;
    }
  | { op: 'remove_role'; role: string }
  | { op: 'map_import'; import: Omit<StudioImport, 'key'> & { key?: string } }
  | { op: 'remove_import'; import: string }
  | { op: 'set_source'; source: string; role: StudioSource['role']; dismissed?: boolean }
  /** Where a part came from, said whole: the list replaces the part's; an empty one forgets it (4.13, 4.14). */
  | { op: 'set_from'; path: string; from: StudioFrom[] }
  | { op: 'note'; note: Omit<StudioNote, 'key'> & { key?: string } }
  | { op: 'remove_note'; note: string }
  /** Sets or clears the Dashboard card a note is answered by; `sources` says whole which sources it cites (4.14). */
  | { op: 'update_note'; note: string; card?: string; sources?: string[]; clear?: Array<'card'> }
  | { op: 'not_yet'; item: Omit<StudioNotYet, 'key'> & { key?: string } }
  | { op: 'remove_not_yet'; item: string }
  | { op: 'dismiss_gap'; gap: string; reason?: string }
  | { op: 'add_check'; check: CriticCheck }
  | { op: 'drop_check'; id: string }
  | { op: 'reopen_gap'; gap: string }
  /** Sources that disagree (9.10): the side the person took. Sent after that side's own ops, it closes the conflict's gap and clears its Decide later. */
  | { op: 'pick_conflict'; conflictId: string; sourceId: string }
  /** Decide later (9.10): the conflict's gap stays open, marked, and the Build sheet asks once more. */
  | { op: 'later_conflict'; conflictId: string }
  /** The map from the app the person uses (6.14): replaces the map with that id; touches `app_map:<id>`. */
  | { op: 'set_app_map'; map: StudioAppMap }
  | { op: 'remove_app_map'; id: string };

export type StudioOp = StudioRecordOp | StudioPartOp;

export const STUDIO_OP_NAMES = [
  'set_workspace',
  'add_record',
  'update_record',
  'rename',
  'remove_record',
  'add_field',
  'update_field',
  'remove_field',
  'move_field',
  'seed_list',
  'add_card',
  'remove_card',
  'add_board_card',
  'remove_board_card',
  'add_role',
  'update_role',
  'remove_role',
  'map_import',
  'remove_import',
  'set_source',
  'set_from',
  'note',
  'remove_note',
  'update_note',
  'not_yet',
  'remove_not_yet',
  'dismiss_gap',
  'add_check',
  'drop_check',
  'reopen_gap',
  'pick_conflict',
  'later_conflict',
  'set_app_map',
  'remove_app_map',
] as const satisfies readonly StudioOp['op'][];
true satisfies CoversAll<StudioOp['op'], typeof STUDIO_OP_NAMES>;

/**
 * A wish the person left out (skipped by a turn, or the bin on its ghost) is
 * kept as a gap left: `not_yet:<key>` in `dismissedGaps`, the wish's need its
 * reason. It is gone from the draft and the canvas, and a later turn reads it
 * as declined, so no stage of the compile notes the same wish again unasked.
 */
export const NOT_YET_LEFT_PREFIX = 'not_yet:';

/** The ops that leave out the wish keyed `key`: it goes, and the draft keeps that it was left (once). Empty for a key the draft lacks. */
export const leaveOutWishOps = (draft: Pick<StudioDraft, 'notYet' | 'dismissedGaps'>, key: string): StudioOp[] => {
  const wish = draft.notYet.find((one) => one.key === key);
  if (wish === undefined) return [];
  const gap = `${NOT_YET_LEFT_PREFIX}${key}`.slice(0, 80);
  const left = draft.dismissedGaps.some((one) => one.gap === gap);
  return [{ op: 'remove_not_yet', item: key }, ...(left ? [] : [{ op: 'dismiss_gap' as const, gap, reason: wish.need.slice(0, STUDIO_LIMITS.reason) }])];
};

/** The wishes the person left out, by their need, oldest first. */
export const leftWishesOf = (draft: Pick<StudioDraft, 'dismissedGaps'>): Array<{ need: string }> =>
  draft.dismissedGaps.flatMap((one) => (one.gap.startsWith(NOT_YET_LEFT_PREFIX) && one.reason !== undefined && one.reason.trim() !== '' ? [{ need: one.reason }] : []));

/**
 * How OS settled a check in the conversation, as `dismissedGaps` keeps its
 * reason (F17 of docs/plans/qa-run-1-review.md): answered by what the turn
 * did, or declined. Only a declined check is one the person left.
 */
export const GAP_SETTLED = { answered: 'answered in the conversation', declined: 'declined in the conversation' } as const;

/** Reasons a gap leaves the list because it was met, never because the person left it: the critic judged it met, or it was answered. */
const GAP_MET_REASONS: ReadonlySet<string> = new Set(['met', GAP_SETTLED.answered]);

/**
 * Whether a gap in `dismissedGaps` was left as it is by the person: tapped
 * Leave it, declined in the conversation, or a wish left out. A check the
 * critic judged met, one answered in the conversation, or a pick
 * (`picked …`) was settled, not left. A check settled before the two were
 * told apart reads as left, as it always did.
 */
export const gapLeftAsIs = (one: { readonly gap: string; readonly reason?: string }): boolean =>
  one.reason === undefined || !(GAP_MET_REASONS.has(one.reason) || one.reason.startsWith('picked '));
